import { specialMACSorting } from "../../scenes/Facility/utils";
import { get, post } from "../../utils/net";

import type { BackgroundJobResponse, FacilitySyncJobResult } from "../../models/backgroundJob";
import type { PaginationList } from "../../models/pagination";
import type {
  CCNDropDownData,
  CCNDropDownRow,
  FacilityEntity,
  FacilityTypeDropDownRow,
  MACDropDownRow,
  NpiCCNRow,
} from "../../scenes/Facility/models/facilities";

const baseURL = process.env.REACT_APP_API_PATH;
const groupingURL = process.env.REACT_APP_TEE_GRP;
const mneURL = process.env.REACT_APP_TEE_MNE;

const listFacilities = (): Promise<FacilityEntity[]> => {
  const url = `${baseURL}/api/Facilities`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result: { body: FacilityEntity[] }) =>
    mapFacilitiesResultToEntity(result.body),
  );
};

const mapFacilityResultToEntity = (facility: FacilityEntity): FacilityEntity => ({
  ...facility,
  Mac: facility.Mac || "",
  NPI: facility.NPI || "",
});

const mapFacilitiesResultToEntity = (result: FacilityEntity[]): FacilityEntity[] => {
  if (!result?.length) {
    return [];
  }

  return result.map((facility) => mapFacilityResultToEntity(facility));
};

const getFacilityTypesDropDown = (): Promise<FacilityTypeDropDownRow[]> => {
  const url = `${groupingURL}/groupinginfo/ccntypes`;
  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result: { body: FacilityTypeDropDownRow[] }) => result.body);
};

const getCCNDropDown = (
  skip: number,
  pageSize: number,
  type: string,
  sortField: string,
  sortDir: string,
  filter: { value: string },
): Promise<CCNDropDownData> => {
  const urlPart = `${groupingURL}/groupinginfo/cmscertificationnumbers/custom?`;
  const params = new URLSearchParams({
    Top: `${pageSize}`,
    Skip: `${skip}`,
    Filter: filter && filter.value ? filter.value : "",
    [`Sort${sortDir || "Asc"}.${sortField || "Ccn"}`]: "1",
    Count: "true",
  });
  type && params.append("Match.Type", type);

  const url = `${urlPart}${params}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result: { body: PaginationList<CCNDropDownRow> }) => ({
    Total: result.body?.TotalCount || 0,
    CMSCertificationNumberInfos: result.body?.Items || [],
  }));
};

export const getCCNListByNpi = async (npi: string): Promise<NpiCCNRow[]> => {
  const url = `${groupingURL}/groupinginfo/ccnlist?npi=${encodeURIComponent(npi)}`;

  const extraOptions = {
    useExactPath: true,
  };

  const result: { body: NpiCCNRow[] } = await get(url, extraOptions);
  return result.body || [];
};

const getMACDropDown = (): Promise<MACDropDownRow[]> => {
  const url = `${mneURL}/MedicalNecessityEdits/FiscalIntermediaries`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result: { body: MACDropDownRow[] }) => {
    const unsorted = result.body;
    const sorted = specialMACSorting(unsorted, [{ field: "Mac", dir: "asc" }]);
    return sorted;
  });
};

const createFacility = (facility: FacilityEntity): Promise<FacilityEntity> => {
  const url = `${baseURL}/api/facilities`;

  const extraOptions = {
    useExactPath: true,
  };

  return post(url, facility, extraOptions).then((result: { body: FacilityEntity }) =>
    mapFacilityResultToEntity(result.body),
  );
};

const updateFacility = (facility: FacilityEntity): Promise<FacilityEntity> => {
  const url = `${baseURL}/api/facilities`;

  const extraOptions = {
    useExactPath: true,
  };

  return post(url, facility, extraOptions).then((result: { body: FacilityEntity }) =>
    mapFacilityResultToEntity(result.body),
  );
};

const getFacility = (facilityId: string): Promise<FacilityEntity> => {
  const url = `${baseURL}/api/facilities/${facilityId}`;

  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result: { body: FacilityEntity }) =>
    mapFacilityResultToEntity(result.body),
  );
};

interface FacilitySyncPayload {
  SourceFacilityId: string;
  TargetFacilityIds: string[];
}

interface BulkCreatePayload {
  BaseFacilityId: string;
  CCNs: string[];
}

export interface BulkCreateJobResult {
  Created: number;
  Skipped: number;
  Errors?: {
    Message: string;
    Count: number;
  }[];
}

const startFacilitySync = (payload: FacilitySyncPayload): Promise<BackgroundJobResponse> => {
  const url = `${baseURL}/api/Facilities/sync`;
  const extraOptions = {
    useExactPath: true,
  };

  return post(url, payload, extraOptions)
    .then((result: { body: BackgroundJobResponse }) => result.body)
    .catch((error: { status: number }) => {
      // Handle 409 Conflict - another sync operation is already in progress
      if (error.status === 409) {
        const conflictError = new Error(
          "Another settings copying operation is already in progress.",
        );
        (conflictError as Error & { status: number }).status = 409;
        throw conflictError;
      }
      throw error;
    });
};

const getFacilitySyncStatus = (): Promise<BackgroundJobResponse<FacilitySyncJobResult>> => {
  const url = `${baseURL}/api/Facilities/sync`;
  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then(
    (result: { body: BackgroundJobResponse<FacilitySyncJobResult> }) => result.body,
  );
};

const getFacilitySyncTargets = (facilityId: string): Promise<string[]> => {
  const url = `${baseURL}/api/Facilities/${facilityId}/synctargets`;
  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result: { body: string[] }) => result.body || []);
};

const startBulkCreate = (payload: BulkCreatePayload): Promise<BackgroundJobResponse> => {
  const url = `${baseURL}/api/Facilities/bulkCreate`;
  const extraOptions = {
    useExactPath: true,
  };

  return post(url, payload, extraOptions).then((result) => result.body);
};

const getBulkCreateStatus = (
  jobId: string,
): Promise<BackgroundJobResponse<BulkCreateJobResult>> => {
  const url = `${baseURL}/api/Facilities/bulkCreate/${jobId}`;
  const extraOptions = {
    useExactPath: true,
  };

  return get(url, extraOptions).then((result) => result.body);
};

export const facilitiesAPI = {
  listFacilities,
  getFacilityTypesDropDown,
  getCCNDropDown,
  getCCNListByNpi,
  getMACDropDown,
  createFacility,
  updateFacility,
  getFacility,
  startFacilitySync,
  getFacilitySyncStatus,
  getFacilitySyncTargets,
  startBulkCreate,
  getBulkCreateStatus,
};
